import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { initAnonymousAnalytics, initFullAnalytics, revokeAnalytics } from '../lib/analytics';

const STORAGE_KEY = 'scanmarket_cookie_consent_v1';
const ACCEPT_TTL_MS  = 60 * 30 * 24 * 60 * 60 * 1000;  // 60 mesi
const REJECT_TTL_MS  =  3 * 30 * 24 * 60 * 60 * 1000;  //  3 mesi

/**
 * CookieConsent state:
 *   { status: 'accepted' | 'rejected' | null, analytics: bool, savedAt: ISO, expiresAt: ISO }
 * null = utente non ha ancora scelto → banner visibile, tracking anonimo attivo.
 */
const CookieConsentContext = createContext({
  status: null,
  analytics: false,
  acceptAll: () => {},
  rejectAll: () => {},
  openBanner: () => {},
  bannerVisible: false,
});

function readStored() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p || !p.expiresAt) return null;
    if (new Date(p.expiresAt).getTime() < Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return p;
  } catch {
    return null;
  }
}

function persist(status, analytics) {
  const now = Date.now();
  const ttl = status === 'accepted' ? ACCEPT_TTL_MS : REJECT_TTL_MS;
  const payload = {
    status,
    analytics: !!analytics,
    savedAt: new Date(now).toISOString(),
    expiresAt: new Date(now + ttl).toISOString(),
  };
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch {}
  return payload;
}

async function logConsent(status) {
  try {
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    await fetch(`${BACKEND}/api/consent/log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, lang: document.documentElement.lang || 'it' }),
      keepalive: true,
    });
  } catch {}
}

export function CookieConsentProvider({ children }) {
  const [state, setState] = useState(() => readStored());
  const [bannerVisible, setBannerVisible] = useState(() => readStored() === null);
  const bootedRef = useRef(false);

  // Boot analytics secondo lo stato corrente
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Attende che PostHog snippet sia caricato (defer async)
    const boot = () => {
      if (bootedRef.current) return;
      if (!window.posthog) {
        // Riprova tra 300ms — script PostHog caricato async dall'index.html
        setTimeout(boot, 300);
        return;
      }
      bootedRef.current = true;
      if (state?.status === 'accepted' && state.analytics) {
        initFullAnalytics();
      } else {
        initAnonymousAnalytics();
      }
    };
    boot();
  }, [state?.status, state?.analytics]);

  const acceptAll = useCallback(() => {
    const p = persist('accepted', true);
    setState(p);
    setBannerVisible(false);
    initFullAnalytics();
    logConsent('accepted');
  }, []);

  const rejectAll = useCallback(() => {
    const p = persist('rejected', false);
    setState(p);
    setBannerVisible(false);
    revokeAnalytics();
    logConsent('rejected');
  }, []);

  const openBanner = useCallback(() => {
    setBannerVisible(true);
  }, []);

  const value = {
    status: state?.status || null,
    analytics: !!state?.analytics,
    expiresAt: state?.expiresAt || null,
    bannerVisible,
    acceptAll,
    rejectAll,
    openBanner,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  return useContext(CookieConsentContext);
}
